import React from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const getScholarshipDisplayName = (scholarship) => {
  const line1 = scholarship?.name || scholarship?.scholarship_name || "";
  const line2 = scholarship?.name_line_2 || scholarship?.scholarship_name_line_2 || "";

  return line2 ? `${line1} - ${line2}` : line1;
};

const ScholarshipSelector = ({
  adminStatus,
  selectedScholarshipId,
  selectedScholarship,
  selectedStudents,
  setOpenDialog,
  setNewScholarship,
  handleScholarshipChange,
  scholarships,
  canDriveSelection,
}) => {

  return (
    <Box sx={{ flex: 5 }}>
      {canDriveSelection ? (
        <FormControl fullWidth size="small">
          <InputLabel id="scholarship-select-label">
            Select Scholarship
          </InputLabel>
          <Select
            labelId="scholarship-select-label"
            label="Select Scholarship"
            value={selectedScholarshipId}
            onChange={(e) => {
              const newId = e.target.value;
              if (selectedStudents.length > 0) {
                setOpenDialog(true);
                setNewScholarship(newId);
              } else {
                handleScholarshipChange(newId);
              }
            }}
            renderValue={(selectedId) => {
              const selected = scholarships.find((s) => s.id === selectedId);
              if (!selected) return "";
              const awardedCount = selected.awardHistory?.length || 0;
              return (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: ".85rem" }}>
                    {getScholarshipDisplayName(selected)} (${selected.award_amount}){" "}
                    {selected.award_type}
                  </span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontWeight: "normal",
                      fontSize: "0.75rem",
                    }}
                  >
                    {awardedCount}/{selected.number_of_awards}
                  </span>
                </Box>
              );
            }}
          >
            {scholarships.map((scholarship) => {
              const awardedCount = scholarship.awardHistory?.length || 0;
              const totalNeeded = scholarship.number_of_awards;
              const isComplete = awardedCount >= totalNeeded;
              const isPartial = awardedCount > 0 && awardedCount < totalNeeded;

              return (
                <MenuItem
                  key={scholarship.id}
                  value={scholarship.id}
                  sx={{
                    backgroundColor: isComplete
                      ? "#E8F5E9"
                      : isPartial
                      ? "#FFF9C4"
                      : "inherit",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontWeight:
                          selectedScholarship?.id === scholarship.id
                            ? "bold"
                            : "normal",
                      }}
                    >
                      {getScholarshipDisplayName(scholarship)} (${scholarship.award_amount}){" "}
                      {scholarship.award_type}
                      {["Scholarship"].includes(scholarship.award_type) ||
                      ["Academic"].includes(scholarship.priority1) ||
                      ["Academic"].includes(scholarship.priority2) ||
                      ["Academic"].includes(scholarship.priority3) ? (
                        <span style={{ marginLeft: "6px" }}>📚</span>
                      ) : null}
                    </Typography>
                    {isComplete && (
                      <CheckIcon
                        fontSize="small"
                        sx={{ color: "green", mr: 1 }}
                      />
                    )}
                    {isPartial && (
                      <WarningAmberIcon
                        fontSize="small"
                        sx={{ color: "orange", mr: 1 }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: "#555", fontSize: "0.75rem", ml: 2 }}
                    >
                      {awardedCount}/{totalNeeded}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      ) : (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {selectedScholarship ? (
            <>
              {selectedScholarship.awardHistory?.length >=
              selectedScholarship.number_of_awards ? (
                <CheckIcon sx={{ color: "green" }} />
              ) : selectedScholarship.awardHistory?.length > 0 ? (
                <WarningAmberIcon sx={{ color: "orange" }} />
              ) : null}
              <Typography sx={{ fontSize: "1.2rem" }}>
                {getScholarshipDisplayName(selectedScholarship)} ($
                {selectedScholarship?.award_amount})
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#555", fontSize: "0.75rem", ml: 2 }}
              >
                {selectedScholarship.awardHistory?.length || 0}/
                {selectedScholarship.number_of_awards}
              </Typography>
            </>
          ) : (
            <Typography variant="body1" style={{ color: "red" }}>
              <em>--Awaiting Scholarship Selection--</em>
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ScholarshipSelector;
